'use client';

import { Job } from '@/Types/JobTypes';
import { MapPin, Clock, Briefcase, Calendar } from 'lucide-react';
import { jobService } from '@/services/jobService';

interface JobCardProps {
  job: Job;
  isSelected?: boolean;
  onClick?: () => void;
}

export default function JobCard({ job, isSelected = false, onClick }: JobCardProps) {
  const isOpen = jobService.isJobOpen(job);
  const daysUntilClosing = jobService.getDaysUntilClosing(job);
  const salaryRange = jobService.formatSalaryRange(job);

  return (
    <div
      onClick={onClick}
      className={`
        flex flex-col p-5 rounded-lg border-2 cursor-pointer transition-all duration-200
        ${isSelected 
          ? 'border-secondary bg-secondary/10' 
          : 'border-gray-200 bg-white hover:border-secondary/50 hover:shadow-md'
        }
      `}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="text-[18px] font-semibold text-primary mb-1 line-clamp-2">
            {job.jobTitle}
          </h3>
          {job.department && (
            <span className="text-[13px] text-gray-600 font-medium">
              {job.department}
            </span>
          )}
        </div>
        
        {/* Status Badge */}
        <div className={`
          px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap ml-2
          ${isOpen 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
          }
        `}>
          {isOpen ? 'Open' : 'Closed'}
        </div>
      </div>

      {/* Job Details */}
      <div className="flex flex-col gap-2 mb-4">
        {/* Location */}
        <div className="flex items-center gap-2 text-[14px] text-gray-700">
          <MapPin className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <span className="line-clamp-1">{job.jobLocation}</span>
        </div>

        {/* Job Type */}
        {job.jobType && (
          <div className="flex items-center gap-2 text-[14px] text-gray-700">
            <Briefcase className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <span>{job.jobType}</span>
          </div>
        )}

        {/* Salary Range */}
        <div className="flex items-center gap-2 text-[14px] text-gray-700">
          <Clock className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <span>{salaryRange}</span>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-200">
        {/* Closing Date */}
        <div className="flex items-center gap-1.5 text-[13px]">
          <Calendar className="w-3.5 h-3.5 text-gray-500" />
          <span className={`
            ${daysUntilClosing <= 7 && isOpen ? 'text-orange-600 font-medium' : 'text-gray-600'}
          `}>
            {isOpen 
              ? `${daysUntilClosing} day${daysUntilClosing !== 1 ? 's' : ''} left`
              : 'Expired'
            }
          </span>
        </div>

        {/* Applications Count */}
        {job.applicationsCount > 0 && (
          <span className="text-[13px] text-gray-500">
            {job.applicationsCount} applicant{job.applicationsCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  );
}